import React, { FC, useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import listStyles from "./virtuallist.module.css";

export interface EventDataInfo {
  text: string;
  id?: string;
  code?: string;
  padding?: string;
}

interface VirtualListProps {
  items: [EventDataInfo];
  estimateSize?: () => number;
  selected?: EventDataInfo;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onKeyDown?: () => void;
}
//eslint-disable-next-line
const VirtualList: FC<VirtualListProps> = ({
  items,
  selected,
  estimateSize = () => 20,
  onClick,
  onDoubleClick,
  onKeyDown,
}) => {
  const parentRef = useRef(null);

  useEffect(() => {
    if (!items.length) return;
    const index = items.findIndex((value) => value.id === selected?.id || value.text === selected?.text);
    if (index) rowVirtualizer.scrollToIndex(index);
    setTimeout(() => {
      const indexToScroll = index === -1 ? 0 : index;
      const elementToFocus = parentRef.current.querySelector(`[tabIndex="${indexToScroll}"]`);
      elementToFocus?.focus();
      elementToFocus?.click();
      console.log(elementToFocus);
    }, 250);
  }, [items]);

  const handleKeyDown = (e: React.KeyboardEvent, data: EventDataInfo) => {
    onKeyDown && onKeyDown(data, e);
  };

  const handleOnClick = (e: React.MouseEvent, data: EventDataInfo) => {
    onClick && onClick(data, e);
  };

  const handleDbClick = (e: React.MouseEvent, data: EventDataInfo) => {
    onDoubleClick && onDoubleClick(data, e);
  };

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: estimateSize,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      className={clsx(listStyles.VirtualListSmeta)}
      style={{
        height: `inherit`,
        overflow: "auto",
      }}
    >
      <div
        className={clsx(listStyles.VirtualListSmetaCointainer)}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
        onKeyDown={(e) => {
          const index: number = e?.target?.tabIndex;
          if (index !== -1) handleKeyDown(e, items[index]);
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          const { id, text, padding } = item;
          const isSelected = selected?.id === id || selected?.text === text;
          return (
            <div
              className={clsx(listStyles.listItem, isSelected && listStyles.listItemSelected)}
              style={{ paddingLeft: padding, transform: `translateY(${virtualRow.start}px)` }}
              title={text}
              key={virtualRow.index}
              tabIndex={virtualRow.index}
              onClick={(e) => {
                handleOnClick(e, item);
              }}
              onDoubleClick={(e) => {
                handleDbClick(e, item);
              }}
            >
              {text}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualList;
